// Per-device preferences, kept in localStorage. Shared settings (retention, muted
// words) live on the server instead.
import { useSyncExternalStore } from "react";
import tokens from "@vladrss/tokens/tokens.json";
import type { Grouping, Order } from "@vladrss/core";

export type ThemeMode = "system" | "dark" | "light";
/** How lists show articles: Compact (title and a little text, no photos) or Previews. */
export type Density = "compact" | "previews";

export interface Prefs {
  theme: ThemeMode;
  palette: "sage" | "slate";
  black: boolean;
  size: number; // index into tokens.type.readSizes
  font: "serif" | "sans";
  measure: number; // index into tokens.type.measure (characters per line)
  density: Density;
  pv: "short" | "long"; // previews of 2 lines and a small photo, or 3 lines and a larger one
  group: Grouping;
  order: Order;
  nav: "full" | "rail";
  listOff: boolean;
}

const DEFAULTS: Prefs = {
  theme: "system", palette: "sage", black: false, size: tokens.type.readSizeDefault, font: "serif", measure: tokens.type.measureDefault,
  density: "previews", pv: "short", group: "time", order: "oldest", nav: "full", listOff: false,
};
const KEY = "vladrss.prefs";

function load(): Prefs {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? "{}");
    // settings saved before line length had five steps, and before previews were short or long
    if (typeof saved.measure === "string") saved.measure = ({ narrow: 0, medium: 1, wide: 2 } as Record<string, number>)[saved.measure] ?? DEFAULTS.measure;
    if (saved.pv === "compact") saved.pv = "short";
    if (saved.pv === "roomy") saved.pv = "long";
    if (saved.density === "titles") saved.density = "compact"; // Titles became Compact
    if (saved.density === "cards") saved.density = "previews"; // Cards are gone
    delete saved.mode; // continuous reading is gone
    return { ...DEFAULTS, ...(saved as Partial<Prefs>) };
  } catch { return { ...DEFAULTS }; }
}

let current = load();
const listeners = new Set<() => void>();

/** These follow the owner to every device (kept on the server); the rest stay with this screen. */
export const SYNCED = ["theme", "palette", "black", "order", "group"] as const;
export type Look = Pick<Prefs, (typeof SYNCED)[number]>;
export const lookOf = (p: Prefs): Look => ({ theme: p.theme, palette: p.palette, black: p.black, order: p.order, group: p.group });
let sendLook: ((l: Partial<Look>) => Promise<unknown>) | null = null;
export function onLookChange(send: ((l: Partial<Look>) => Promise<unknown>) | null) { sendLook = send; }

export function setPrefs(patch: Partial<Prefs>, remote = false) {
  const before = current;
  current = { ...current, ...patch };
  if (!remote && sendLook) {
    const changed = Object.fromEntries(SYNCED.filter((k) => before[k] !== current[k]).map((k) => [k, current[k]])) as Partial<Look>;
    // Start the optimistic mutation synchronously; Convex owns acknowledgement/rebase/rollback.
    if (Object.keys(changed).length) void sendLook(changed).catch(() => {});
  }
  try { localStorage.setItem(KEY, JSON.stringify(current)); } catch { /* private window: keep in memory */ }
  applyAppearance();
  listeners.forEach((l) => l());
}

export const getPrefs = () => current;
export function usePrefs(): Prefs {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => current);
}

const light = matchMedia("(prefers-color-scheme: light)");
export const effectiveMode = (p = current) => (p.theme === "system" ? (light.matches ? "light" : "dark") : p.theme);

/** Theme and reading settings become attributes and CSS variables on <html>. */
export function applyAppearance() {
  const r = document.documentElement, p = current;
  r.dataset.palette = p.palette;
  r.dataset.mode = effectiveMode(p);
  if (p.black) r.dataset.black = ""; else delete r.dataset.black;
  r.style.setProperty("--read-size", `${tokens.type.readSizes[p.size] ?? 19}px`);
  r.style.setProperty("--read-font", p.font === "sans" ? "var(--f-ui)" : "var(--f-read)");
  r.style.setProperty("--read-lh", String(p.font === "sans" ? tokens.type.lineHeight.sans : tokens.type.lineHeight.serif));
  r.style.setProperty("--measure", `${tokens.type.measure[p.measure] ?? 66}ch`);
  r.classList.toggle("pv-long", p.pv === "long");
  const bg = getComputedStyle(r).getPropertyValue("--bg").trim();
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", bg || "#131311");
}
light.addEventListener("change", () => { if (current.theme === "system") { applyAppearance(); listeners.forEach((l) => l()); } });

/** The sources sidebar button and "[". */
export function toggleNav(): void { setPrefs({ nav: getPrefs().nav === "full" ? "rail" : "full" }); }
